/**
 * Provides the `addParametersToURL` utility function for adding parameters to a URL string, and its associated `AddParametersToUrlOptions` and `AddParametersToUrlFinalOptions` types.
 * @module addParametersToURL
 */
// Import global utils
import { isEmpty } from '@automator/shared/utils/is-empty';
/**
 * Inserts or updates parameters in a URL string, optionally modifying the browser's history or location.
 *
 * **Default behavior:**
 * By default (when no `options` are provided):
 * - Takes a URL string and an object of parameters (`{ key: value }`).
 * - If the input `url` is empty or null/undefined, it attempts to use the current `window.location.href` (if available in the environment). If still unavailable, returns the original empty/invalid input.
 * - Parses the effective URL string. If parsing fails, returns the original invalid URL string.
 * - Iterates through the provided `parameters` object.
 * - For each parameter:
 * - Encodes the key and value using `encodeURIComponent`. Null/undefined values are encoded as empty strings (`''`).
 * - If a parameter with the same key already exists in the URL, its value is updated.
 * - If the parameter key does not exist, it's added to the end of the query string.
 * - Filters out any resulting parameters that have an empty value part (e.g., `key=` resulting from a null/undefined/empty string input value).
 * - Reconstructs the URL with the modified query string, preserving the original path, hash, etc.
 * - Does *not* modify the browser's current URL or history.
 * - Returns the modified URL string.
 *
 * **Options:**
 * The function accepts an `options` object to control *browser-specific* side effects:
 * - `setAsURL: true`: Updates the browser's address bar using `history.pushState` without reloading the page.
 * - `setAsURL: true, refresh: true`: Navigates the browser to the new URL, causing a page reload (`window.location.href = ...`).
 *
 * @since 3.5 Initial implementation.
 * @param {string} [url=''] - The URL string to modify. Defaults to `window.location.href` if empty and in a browser context.
 * @param {UrlParameters} [parameters={}] - An object containing key-value pairs for the parameters to add or update. Values can be `string`, `number`, `boolean`, `null`, or `undefined`.
 * @param {AddUrlParametersOptions} [options={}] - Configuration options.
 * @param {boolean} [options.setAsURL=false] - If `true`, updates the browser's address bar using `history.pushState` without reloading the page.
 * @param {boolean} [options.refresh=false] - If `true` AND `options.setAsURL` is also `true`, navigates the browser to the new URL, causing a page reload (`window.location.href = ...`). This option has no effect if `setAsURL` is `false`.
 * @return {string} The modified URL string. Returns the original `url` if it's invalid or could not be processed.
 *
 * @example Importing the addParametersToURL utility
 * ```typescript
 * import { addParametersToURL } from '@automator/shared/utils/add-parameters-to-url';
 * ```
 *
 * @example Adding a single parameter
 * ```typescript
 * const originalUrl = 'https://automatorplugin.com/path';
 * const params = { key1: 'value1' };
 * addParametersToURL( originalUrl, params );
 * // => "https://automatorplugin.com/path?key1=value1"
 * ```
 *
 * @example Adding multiple parameters
 * ```typescript
 * const originalUrl = 'https://automatorplugin.com';
 * const params = { key1: 'value1', key2: 'value2' };
 * addParametersToURL( originalUrl, params );
 * // => "https://automatorplugin.com/?key1=value1&key2=value2"
 * ```
 *
 * @example Updating an existing parameter
 * ```typescript
 * const originalUrl = 'https://automatorplugin.com/path?key1=old&key2=keep';
 * const params = { key1: 'new' };
 * addParametersToURL( originalUrl, params );
 * // => "https://automatorplugin.com/path?key1=new&key2=keep"
 * ```
 *
 * @example Adding and updating simultaneously
 * ```typescript
 * const originalUrl = 'https://automatorplugin.com/path?update_me=old';
 * const params = { update_me: 'new', add_me: 'added' };
 * addParametersToURL( originalUrl, params );
 * // => "https://automatorplugin.com/path?update_me=new&add_me=added"
 * ```
 *
 * @example Encoding special characters
 * ```typescript
 * const originalUrl = 'https://automatorplugin.com';
 * const params = { 'spaced key': 'value with & ?' };
 * addParametersToURL( originalUrl, params );
 * // => "https://automatorplugin.com/?spaced%20key=value%20with%20%26%20%3F"
 * ```
 *
 * @example Handling different value types
 * ```typescript
 * const originalUrl = 'https://automatorplugin.com';
 * const params = { count: 5, active: true, ratio: 0.5, empty: '', missing: null };
 * addParametersToURL( originalUrl, params );
 * // => "https://automatorplugin.com/?count=5&active=true&ratio=0.5" (empty/null params are removed)
 * ```
 *
 * @example Using default URL (window.location)
 * ```typescript
 * // Assuming window.location.href is 'http://localhost/current?page=1'
 * const params = { sort: 'asc' };
 * addParametersToURL( '', params ); // Pass empty string for URL
 * // => "http://localhost/current?page=1&sort=asc"
 * ```
 *
 * @example Preserving hash fragment
 * ```typescript
 * const originalUrl = 'https://automatorplugin.com/path?query=1#section2';
 * const params = { newParam: 'value' };
 * addParametersToURL( originalUrl, params );
 * // => "https://automatorplugin.com/path?query=1&newParam=value#section2"
 * ```
 *
 * @example Removing a parameter by setting value to null/undefined/empty
 * ```typescript
 * const originalUrl = 'https://automatorplugin.com/path?a=1&b=2';
 * const params = { b: null };
 * addParametersToURL( originalUrl, params );
 * // => "https://automatorplugin.com/path?a=1"
 * ```
 *
 * @example Using options to update browser URL (no refresh)
 * ```typescript
 * // Assuming window.location.href is 'http://localhost/page'
 * const params = { filter: 'active' };
 * const options = { setAsURL: true };
 * addParametersToURL( '', params, options );
 * // Returns: "http://localhost/page?filter=active"
 * // Side effect: Browser address bar updates to the new URL via history.pushState.
 * ```
 *
 * @example Using options to update browser URL (with refresh)
 * ```typescript
 * // Assuming window.location.href is 'http://localhost/page'
 * const params = { status: 'done' };
 * const options = { setAsURL: true, refresh: true };
 * addParametersToURL( '', params, options );
 * // Returns: "http://localhost/page?status=done"
 * // Side effect: Browser navigates to the new URL, causing a page reload.
 * ```
 *
 * @remarks
 * - This function relies on the standard `URL` API, which might not be available in very old environments.
 * - The browser side effects (`setAsURL`, `refresh`) only occur if `window` and `document` are defined.
 * - For comprehensive behavior details, refer to the unit tests.
 */
export function addParametersToURL(url = '', parameters = {}, options = {}) {
    let effectiveUrl = url;
    // If the URL is empty, use the site URL (only works in browser context)
    if (isEmpty(effectiveUrl) && typeof window !== 'undefined' && window.location) {
        effectiveUrl = window.location.href;
    }
    // If still empty or no window context, return original/empty string
    if (isEmpty(effectiveUrl)) {
        return url;
    }
    let parsedUrl;
    // Try to get a URL object
    try {
        parsedUrl = new URL(effectiveUrl);
    }
    catch (e) {
        // If something goes wrong (invalid URL), just return the original URL string
        console.error('Failed to parse URL:', effectiveUrl, e);
        return effectiveUrl;
    }
    // Get the current parameters from the URL's search string
    // Filter out empty strings that can result from split('&') on '?' or '?&'
    let urlParameters = parsedUrl.search
        ? parsedUrl.search.substring(1).split('&').filter(Boolean)
        : [];
    // Iterate through the parameters to add/update
    for (const [key, value] of Object.entries(parameters)) {
        const encodedKey = encodeURIComponent(key);
        // Ensure value is a string before encoding
        const encodedValue = encodeURIComponent((value === null || value === undefined) ? '' : String(value));
        let found = false;
        // Check if parameter already exists
        for (let i = 0; i < urlParameters.length; i++) {
            const [existingKey] = urlParameters[i].split('=');
            if (existingKey === encodedKey) {
                // Update existing parameter
                urlParameters[i] = `${encodedKey}=${encodedValue}`;
                found = true;
                break;
            }
        }
        // If parameter was not found, add it
        if (!found) {
            urlParameters.push(`${encodedKey}=${encodedValue}`);
        }
    }
    // Filter out any potential empty parameters or parameters without values that might have existed initially or resulted from logic errors
    urlParameters = urlParameters.filter(parameter => {
        const parameterParts = parameter.split('=');
        // Ensure parameter string isn't empty and has a value part
        return !isEmpty(parameter) && parameterParts.length > 1 && !isEmpty(parameterParts[1]);
    });
    // Reconstruct the search part of the URL
    parsedUrl.search = urlParameters.join('&');
    const finalUrlString = parsedUrl.toString();
    // --- Browser-specific ---
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
        // Set default options
        const finalOptions = {
            setAsURL: false,
            refresh: false,
            ...options
        };
        // Check if we need to update the browser's URL
        if (finalOptions.setAsURL) {
            if (finalOptions.refresh) {
                // Reload the page with the new URL
                window.location.href = finalUrlString;
            }
            else {
                // Update URL using History API without reloading
                const pageTitle = document.title; // Get title of the current page
                window.history.pushState({}, pageTitle, finalUrlString);
            }
        }
    }
    // --- End browser-specific ---
    // Return the modified URL string
    return finalUrlString;
}
;
